import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { SEARCH_QUERY_CONFIG } from "../config/queryConfig";
import { searchProduct } from "../services/searchProductService";
import { useSearchStore } from "../store/useSearchStore";
import { Product } from "../types/products";
import { isAmazonUrl, extractAmazonProductId } from "../utils/amazonUrlParser";

interface UseSearchOptions {
  query: string;
  retailer: string;
  page: number;
}

export const useSearch = ({ query, retailer, page }: UseSearchOptions) => {
  const { setCurrentSearch, addToHistory } = useSearchStore();
  const [allResults, setAllResults] = useState<Product[]>([]);
  const navigate = useNavigate();
  
  // ✅ FIX: Track mounted state to prevent memory leaks
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Check if query is an Amazon URL and navigate to product page
  useEffect(() => {
    if (query && isAmazonUrl(query)) {
      // First try to extract product ID directly
      const urlInfo = extractAmazonProductId(query);
      if (urlInfo && urlInfo.productId) {
        // Navigate to product page with Amazon retailer
        navigate(`/product/${urlInfo.productId}?retailer=amazon`);
      }
    }
  }, [query, navigate]);

  // Set current search in store and reset results for new search
  useEffect(() => {
    if (query && retailer && isMountedRef.current) {
      setCurrentSearch(query, retailer);
      // Reset results when starting a new search (page 1) OR when query changes
      if (page === 1) {
        setAllResults([]);
      }
    }
  }, [query, retailer, setCurrentSearch, page]);

  // Reset results when query changes (new search)
  useEffect(() => {
    if (isMountedRef.current) {
      setAllResults([]);
    }
  }, [query, retailer]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["search", retailer, page, query],
    queryFn: () =>
      searchProduct({
        retailer,
        page,
        query,
      }),
    enabled: Boolean(query) && Boolean(retailer),
    ...SEARCH_QUERY_CONFIG, // ✅ FIX: Added retry logic and cache configuration
  });

  // ✅ FIX: Accumulate results when new data comes in (with memory leak protection)
  useEffect(() => {
    if (!isMountedRef.current) return;
    
    const newResults = data?.data?.results;
    if (newResults && isMountedRef.current) {
      if (page === 1) {
        // First page - replace all results
        setAllResults(newResults);
      } else {
        // Subsequent pages - add to existing results, avoiding duplicates
        setAllResults((prev) => {
          // ✅ Also improved: Use Set for O(1) duplicate checking
          const existingIds = new Set(prev.map((p) => p.product_id));
          const uniqueNewResults = newResults.filter(
            (p) => !existingIds.has(p.product_id),
          );
          return uniqueNewResults.length > 0 ? [...prev, ...uniqueNewResults] : prev;
        });
      }
    }
  }, [data?.data?.results, page]);

  // Add to search history when results are loaded
  useEffect(() => {
    if (data?.data?.results && query && retailer && isMountedRef.current) {
      addToHistory({
        query,
        retailer,
        resultCount: data.data.results.length,
      });
    }
  }, [data, query, retailer, addToHistory]);

  return {
    data,
    isLoading,
    error,
    refetch,
    results: allResults, // Return accumulated results
  };
};
